import { FieldValues, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import useProductStore from "../store/productStore";
import Product from "../interfaces/product";
import useEditProduct from "../hooks/useEditProduct";
import useRegisterProduct from "../hooks/useRegisterProduct";
import useCategory from "../hooks/useCategory";
import { useEffect } from "react";
import dayjs from "dayjs";

const ProductForm = () => {
  const imagePathRegex = /^(\/?[a-z0-9_/.-]+)?\.(gif|jpg|png|bmp|jpeg|webp)$/i;
  const dayRegex = /^[0-9]{2}\/[0-9]{2}\/[0-9]{4}$/;

  const schema = z.object({
    name: z
      .string()
      .min(3, { message: "Name must have at least 3 characters" })
      .max(50, { message: "Name must have at most 50 characters" }),

    description: z.string().min(1, { message: "Description can't be empty" }),
    category: z.number().refine((value) => value > 0, {
      message: "Category must be selected",
    }),
    quantity: z.number().min(1, { message: "Amount can't be less than 1" }),
    price: z.number().min(0, { message: "Price can't be negative" }),
    isLimited: z.boolean().default(false),
    /*file: z
      .instanceof(FileList)
      .refine((value) => value.length === 0 || value[0]?.type === "image/png", {
        message: "File must be a png image",
      })
      .optional(),*/
    pathImage: z
      .string()
      .regex(imagePathRegex, { message: "Path must be a valid image path" }),
    postedAt: z
      .string()
      .regex(dayRegex, { message: "Date must be in the format dd/mm/yyyy" }),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    mode: "onSubmit",
    defaultValues: {
      name: "",
      description: "",
    },
  });

  const selectedProduct = useProductStore((s) => s.selectedProduct);
  const setSelectedProduct = useProductStore((s) => s.setSelectedProduct);

  const labelStyle = { marginRight: "10px" };

  const { mutate: editProduct, error: editError } = useEditProduct();
  const { mutate: registerProduct, error: registerError } =
    useRegisterProduct();
  const { data } = useCategory();

  useEffect(() => {
    if (selectedProduct.id !== undefined) {
      setValue("name", selectedProduct.name);
      setValue("description", selectedProduct.description);
      setValue("category", selectedProduct.category.id!);
      setValue("quantity", selectedProduct.quantity);
      setValue("price", selectedProduct.price);
      setValue("isLimited", selectedProduct.limitedAmount);
      setValue("pathImage", selectedProduct.pathImage);
      setValue(
        "postedAt",
        dayjs(selectedProduct.postedAt).format("DD/MM/YYYY")
      );
    }
  }, [selectedProduct, setValue]);
  useEffect(() => {
    if (selectedProduct.id === undefined) {
      setValue("name", "");
      setValue("description", "");
      setValue("category", -1);
      setValue("quantity", 0);
      setValue("price", 0);
      setValue("isLimited", false);
      setValue("pathImage", "");
      setValue("postedAt", "");
    }
  });

  const onSubmit = ({
    name,
    description,
    category,
    quantity,
    price,
    isLimited,
    pathImage,
    postedAt,
  }: FieldValues) => {
    const product: Product = {
      id: selectedProduct.id,
      name: name,
      description: description,
      category: { id: category, name: "", slug: "" },
      quantity: quantity,
      price: price,
      limitedAmount: isLimited,
      pathImage: pathImage,
      users: [],
      postedAt: new Date(
        postedAt.split("/")[2],
        postedAt.split("/")[1],
        postedAt.split("/")[0]
      ),
    };
    selectedProduct.id !== undefined
      ? editProduct(product)
      : registerProduct(product);
    if (selectedProduct.id !== undefined) {
      setSelectedProduct({} as Product);
    }
    reset();

  };

  if (registerError) throw registerError;
  if (editError) throw editError;

  console.log(errors);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div>
          <label htmlFor="name" style={labelStyle}>
            Name:{" "}
          </label>
          <input
            type="text"
            id="name"
            {...register("name")}
            className={
              errors.name
                ? "form-control form-control-sm is-invalid"
                : "form-control form-control-sm"
            }
          />
          <div className="invalid-feedback">{errors.name?.message}</div>
        </div>
        <div>
          <label htmlFor="description" style={labelStyle}>
            Description:{" "}
          </label>
          <input
            type="text"
            id="description"
            {...register("description")}
            className={
              errors.description
                ? "form-control form-control-sm is-invalid"
                : "form-control form-control-sm"
            }
          />
          <div className="invalid-feedback">{errors.description?.message}</div>
        </div>
        <div>
          <label htmlFor="description" style={labelStyle}>
            Category:{" "}
          </label>
          <select
            id="category"
            {...register("category", { valueAsNumber: true })}
            className={
              errors.category
                ? "form-control form-control-sm is-invalid"
                : "form-control form-control-sm"
            }
          >
            {/*data && (
              <>
                <option value={-1}>Select a category</option>
                {data?.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </>
            )*/}
          </select>
          <div className="invalid-feedback">{errors.category?.message}</div>
        </div>

        <div>
          <label htmlFor="quantity" style={labelStyle}>
            Amount:{" "}
          </label>
          <input
            type="number"
            id="quantity"
            {...register("quantity", { valueAsNumber: true })}
            className={
              errors.quantity
                ? "form-control form-control-sm is-invalid"
                : "form-control form-control-sm"
            }
          />
          <div className="invalid-feedback">{errors.quantity?.message}</div>
        </div>
        <div>
          <label htmlFor="price" style={labelStyle}>
            Price:{" "}
          </label>
          <input
            type="number"
            id="price"
            step="0.01"
            {...register("price", { valueAsNumber: true })}
            className={
              errors.price
                ? "form-control form-control-sm is-invalid"
                : "form-control form-control-sm"
            }
          />
          <div className="invalid-feedback">{errors.price?.message}</div>
        </div>
        <div>
          <label htmlFor="isLimited" style={labelStyle}>
            Is Limited:{" "}
          </label>
          <input type="checkbox" id="isLimited" {...register("isLimited")} />
        </div>

        {/*selectedProduct.name === undefined && (
          <div>
            <label htmlFor="file" style={labelStyle}>
              Upload File:{" "}
            </label>
            <input
              type="file"
              id="file"
              accept="image/png"
              {...register("file")}
            />
            <div className="invalid-feedback">{errors.file?.message}</div>
          </div>
        )*/}
        <div>
          <label htmlFor="pathImage" style={labelStyle}>
            Path to Image:{" "}
          </label>
          <input
            type="text"
            id="pathImage"
            {...register("pathImage")}
            className={
              errors.pathImage
                ? "form-control form-control-sm is-invalid"
                : "form-control form-control-sm"
            }
          />
          <div className="invalid-feedback">{errors.pathImage?.message}</div>
        </div>
        <div>
            <label htmlFor="postedAt" style={labelStyle}>
                Posted At:{" "}
            </label>
            <input
                type="text"
                id="postedAt"
                {...register("postedAt")}
                className={
                    errors.postedAt
                        ? "form-control form-control-sm is-invalid"
                        : "form-control form-control-sm"
                }
            />
            <div className="invalid-feedback">{errors.postedAt?.message}</div>
        </div>

        <button id="submitButton" type="submit" className="btn-success">
          {selectedProduct.name !== undefined ? "Edit" : "Register"}
        </button>
        {selectedProduct.name !== undefined && (
          <button
            id="cancelButton"
            className="btn-danger"
            onClick={() => {
              reset();
              useProductStore.getState().setSelectedProduct({} as Product);
            }}
          >
            Cancel
          </button>
        )}
      </form>
    </>
  );
};

export default ProductForm;
